import { Fragment, useMemo, useState, useRef, useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useDndMonitor } from '@dnd-kit/core'
import { AddIcon, ChevronDownIcon, DragHandleIcon } from '@gocanvas/gcds/icons'
import { GCDSButton } from '@gocanvas/gcds/components'
import { PdfElement } from 'apps/studio/components/pdfViewer/elements/PdfElement'
import { ResizeHandle } from 'apps/studio/components/pdfViewer/elements/ResizeHandle'
import { Draggable } from 'apps/studio/components/dragAndDrop/Draggable'
import useOnOutsideClick from 'hooks/useOnOutsideClick'
import {
  getTableCellPosition,
  getColumnHeaderRowData,
  getTableDisplayMetrics,
  getTableBackgroundCss,
  resolveTableSheets,
  entriesForTableSheets,
  buildTextEntryType,
  buildAddColumnPayload,
  buildDeleteColumnPayload,
  buildAddRowPayload,
  buildDeleteRowPayload,
} from 'apps/studio/lib/utils/entities'
import {
  activeSheetSelector,
  entriesSelector,
} from 'apps/formsCreationKit/store/selectors/entities'
import { activeEntryGuidSelector } from 'apps/formsCreationKit/store/selectors/settings'
import { alphaEntryTypesSelector } from 'apps/formsCreationKit/store/selectors/types'
import { pdfPageMetaByPageNumberSelector } from 'apps/studio/store/selectors/pdfPageMeta'
import {
  addTableColumn,
  deleteTableColumn,
  addTableRow,
  deleteTableRow,
  moveTable,
  selectElement,
} from 'apps/studio/store/actions/sharedActions'
import { updateEntry } from 'apps/formsCreationKit/store/actions/sharedActions'
import { appSettingsActions } from 'apps/studio/store/reducers/settings/appSettings'
import { calculateTableDividerResize } from 'apps/studio/lib/utils/dnd'
import { backgroundStyleFromStyles } from 'apps/studio/lib/utils/color'
import { usePositionHotkeys } from 'apps/studio/lib/hooks/usePositionHotkeys'
const { selectSheet } = appSettingsActions

const renderColumnDividers = (displayLayout, tableLayout, tableWrapperId) => {
  let totalX = 0
  return (displayLayout.columns || []).map((col, i) => {
    totalX += col.width
    return (
      <div
        key={`col-divider-${i}`}
        className="static-table--col-resize"
        style={{ '--divider-x': `${totalX}px` }}
      >
        <Draggable
          id={`table-${tableLayout.id}-col-divider-${i}`}
          classes="static-table--resize-grip"
          data={{
            type: 'tableDividerResize',
            axis: 'col',
            index: i,
            tableLayout,
            wrapperId: tableWrapperId,
          }}
        />
      </div>
    )
  })
}

const renderColumnHeaderDivider = (tableLayout, tableWrapperId, dividerY) => (
  <div
    key="col-header-divider"
    className="static-table--row-resize"
    style={{ '--divider-y': `${dividerY}px` }}
  >
    <Draggable
      id={`table-${tableLayout.id}-col-header-divider`}
      classes="static-table--resize-grip"
      data={{
        type: 'tableDividerResize',
        axis: 'columnHeader',
        tableLayout,
        wrapperId: tableWrapperId,
      }}
    />
  </div>
)

const renderRowDividers = (
  displayLayout,
  tableLayout,
  tableWrapperId,
  { titleHeight, columnHeaderHeight } = {}
) => {
  let totalY = (titleHeight ?? 0) + (columnHeaderHeight ?? 0)
  return (displayLayout.rows || []).map((row, i) => {
    totalY += row.height
    return (
      <div
        key={`row-divider-${i}`}
        className="static-table--row-resize"
        style={{ '--divider-y': `${totalY}px` }}
      >
        <Draggable
          id={`table-${tableLayout.id}-row-divider-${i}`}
          classes="static-table--resize-grip"
          data={{
            type: 'tableDividerResize',
            axis: 'row',
            index: i,
            tableLayout,
            wrapperId: tableWrapperId,
          }}
        />
      </div>
    )
  })
}

const StaticTableButton = ({ className, onClick }) => (
  <div className={className} onClick={e => e.stopPropagation()}>
    <GCDSButton LeadingIcon={AddIcon} onClick={onClick} />
  </div>
)

const CellMenu = ({
  position,
  options,
  onSelect,
  isOpen,
  onToggle,
  onClose,
}) => (
  <div
    className={`static-table--cell-chevron${isOpen ? ' is-open' : ''}`}
    style={{
      '--chevron-x': `${position.x + position.width}px`,
      '--chevron-y': `${position.y}px`,
    }}
    onClick={e => e.stopPropagation()}
  >
    <GCDSButton LeadingIcon={ChevronDownIcon} onClick={onToggle} />
    {isOpen && (
      <ul className="static-table--cell-menu" role="menu">
        {options.map(option => (
          <li key={option.value}>
            <button
              type="button"
              onClick={() => {
                onSelect(option)
                onClose()
              }}
            >
              {option.label}
            </button>
          </li>
        ))}
      </ul>
    )}
  </div>
)

export const PdfTable = ({
  tableLayout,
  tableLayoutIds,
  thisPage,
  sheets,
  allStyledElements,
}) => {
  const dispatch = useDispatch()

  const frameRef = useRef(null)
  const [openMenuKey, setOpenMenuKey] = useState(null)
  const closeMenu = useCallback(() => setOpenMenuKey(null), [])
  useOnOutsideClick(frameRef, closeMenu)

  // Update divider preview state while a column/row divider is being dragged.
  const [dividerPreview, setDividerPreview] = useState(null)
  const [isTableDragging, setIsTableDragging] = useState(false)
  const isOwnDividerDrag = active =>
    active?.data?.current?.type === 'tableDividerResize' &&
    active.data.current.tableLayout?.id === tableLayout.id
  const isOwnTableDrag = active =>
    active?.data?.current?.type === 'tableMove' &&
    active.data.current.tableLayout?.id === tableLayout.id
  useDndMonitor({
    onDragStart: ({ active }) => {
      if (isOwnTableDrag(active)) setIsTableDragging(true)
    },
    onDragMove: ({ active, over, delta }) => {
      if (!over || !isOwnDividerDrag(active)) return
      const { layout } = calculateTableDividerResize({ active, over, delta })
      setDividerPreview(layout)
    },
    onDragEnd: ({ active }) => {
      if (isOwnDividerDrag(active)) setDividerPreview(null)
      if (isOwnTableDrag(active)) setIsTableDragging(false)
    },
    onDragCancel: ({ active }) => {
      if (isOwnDividerDrag(active)) setDividerPreview(null)
      if (isOwnTableDrag(active)) setIsTableDragging(false)
    },
  })

  // Show row/column resize preview while a divider is being dragged.
  const displayLayout = dividerPreview
    ? { ...tableLayout, ...dividerPreview }
    : tableLayout

  const activeSheet = useSelector(activeSheetSelector) || {}
  const activeEntryGuid = useSelector(activeEntryGuidSelector)
  const { section_id: activeSectionId, multi_section: activeMultiSection } =
    activeSheet
  const isActiveTable = [activeSectionId, activeMultiSection].includes(
    tableLayout.section_id
  )

  const elementsByLayoutId = allStyledElements.reduce((map, el) => {
    if (!tableLayoutIds.has(el.layout_id)) return map
    const list = map.get(el.layout_id) || []
    list.push(el)
    map.set(el.layout_id, list)
    return map
  }, new Map())

  const { x = 0, y = 0, width = 0, height = 0 } = displayLayout.position || {}
  const cells = (elementsByLayoutId.get(tableLayout.id) || [])
    .slice()
    .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))
  // A column element shares the table's section_id, so selecting one flips
  // isActiveTable true. Suppress the whole-table chrome in that case.
  const isCellElementActive = cells.some(c => c.entry_guid === activeEntryGuid)
  const showTableOrElement = isActiveTable && !isCellElementActive

  const tableWrapperId = `static-table--frame-${tableLayout.id}`
  const tableResizeProps = {
    wrapperId: tableWrapperId,
    position: tableLayout.position,
    targetType: 'table',
    tableLayout,
  }

  const { keyFieldSheet, tableSheet } = useMemo(
    () => resolveTableSheets(sheets, tableLayout.section_id),
    [sheets, tableLayout.section_id]
  )
  const { id: keyFieldSheetId } = keyFieldSheet || {}
  const {
    showColumnHeader,
    hideTableTitle,
    titleHeight,
    columnHeaderHeight,
    displayHeight,
  } = getTableDisplayMetrics(displayLayout)
  const tableBackground = getTableBackgroundCss(displayLayout)
  const frameVars = {
    '--st-x': `${x}px`,
    '--st-y': `${y}px`,
    '--st-w': `${width}px`,
    '--st-h': `${displayHeight || height}px`,
    '--st-title-h': `${titleHeight}px`,
    ...(tableBackground ? { '--st-bg': tableBackground } : {}),
  }
  const allEntries = useSelector(entriesSelector)
  const tableEntries = useMemo(
    () => entriesForTableSheets(allEntries, keyFieldSheet, tableSheet),
    [allEntries, keyFieldSheet, tableSheet]
  )

  const entryTypes = useSelector(alphaEntryTypesSelector)
  const textEntryType = useMemo(
    () => buildTextEntryType(entryTypes),
    [entryTypes]
  )

  const pageMeta = useSelector(
    pdfPageMetaByPageNumberSelector(thisPage?.page_number)
  )
  const pageWidth = pageMeta?.originalWidth ?? 0

  const colCount = displayLayout.columns?.length || 0
  const rowCount = displayLayout.rows?.length || 0
  const tableKeyboardPosition = useMemo(
    () => ({
      ...tableLayout.position,
      height: displayHeight,
    }),
    [tableLayout.position, displayHeight]
  )

  const handleMove = useCallback(
    newPosition => {
      dispatch(
        moveTable({
          layout: {
            ...tableLayout,
            position: {
              ...tableLayout.position,
              ...newPosition,
            },
          },
          fromPage: thisPage,
          toPage: thisPage,
          sheet_id: keyFieldSheetId,
        })
      )
    },
    [dispatch, tableLayout, thisPage, keyFieldSheetId]
  )

  usePositionHotkeys({
    isActive: showTableOrElement,
    position: tableKeyboardPosition,
    pageDimensions: pageMeta || {},
    onMove: handleMove,
  })

  const addColumnAt = insertAt => {
    if (!tableSheet || !textEntryType) return
    const payload = buildAddColumnPayload({
      tableLayout,
      tableSheet,
      entries: tableEntries,
      headerElements: cells,
      insertAt,
      pageWidth,
      entryType: textEntryType,
    })
    if (payload) {
      dispatch(addTableColumn(payload))
    }
  }

  // appends a column / row at the end.
  const handleAddColumnRight = () => addColumnAt(colCount)
  const handleAddRowBelow = () => {
    const payload = buildAddRowPayload({ tableLayout, insertAt: rowCount })
    if (payload) dispatch(addTableRow(payload))
  }

  const cellMenuOptions = (colIdx, rowIdx) =>
    [
      colIdx > 0 && { value: 'col-left', label: 'Add column left' },
      { value: 'col-right', label: 'Add column right' },
      colIdx > 0 &&
        colCount > 2 && { value: 'col-delete', label: 'Delete column' },
      rowIdx > 0 && { value: 'row-above', label: 'Add row above' },
      { value: 'row-below', label: 'Add row below' },
      rowIdx > 0 &&
        rowCount > 2 && { value: 'row-delete', label: 'Delete row' },
    ].filter(Boolean)

  const handleCellMenu = (option, colIdx, rowIdx) => {
    switch (option.value) {
      case 'col-left':
        return addColumnAt(colIdx)
      case 'col-right':
        return addColumnAt(colIdx + 1)
      case 'col-delete': {
        if (!tableSheet) return
        const payload = buildDeleteColumnPayload({
          tableLayout,
          tableSheet,
          entries: tableEntries,
          headerElements: cells,
          colIdx,
        })
        if (payload) dispatch(deleteTableColumn(payload))
        return
      }
      case 'row-above':
      case 'row-below': {
        const insertAt = option.value === 'row-above' ? rowIdx : rowIdx + 1
        const payload = buildAddRowPayload({ tableLayout, insertAt })
        if (payload) dispatch(addTableRow(payload))
        return
      }
      case 'row-delete': {
        const payload = buildDeleteRowPayload({ tableLayout, rowIdx })
        if (payload) dispatch(deleteTableRow(payload))
        return
      }
    }
  }

  const onClick = e => {
    e.stopPropagation()
    closeMenu()
    if (keyFieldSheet?.id) {
      dispatch(selectSheet({ sheet_id: keyFieldSheet.id }))
    }
  }

  const renderColumnHeaderRow = () => {
    if (!showColumnHeader) return null
    const headerRow = getColumnHeaderRowData(displayLayout, cells, tableEntries)
    if (!headerRow.length) return null

    return headerRow.map(({ colIdx, element, entry, position, label }) => (
      <div
        key={`col-header-${colIdx}`}
        className="static-table--cell static-table--column-header"
        style={{
          '--cell-x': `${position.x}px`,
          '--cell-y': `${position.y}px`,
          '--cell-w': `${position.width}px`,
          '--cell-h': `${position.height}px`,
        }}
        onClick={e => {
          e.stopPropagation()
          closeMenu()
          if (element?.entry_guid) {
            dispatch(
              selectElement({
                entry_guid: element.entry_guid,
                sheet_id: entry?.sheet_id,
              })
            )
          }
        }}
      >
        <input
          className="static-table--column-header-input"
          value={label}
          onChange={e => {
            if (!element?.entry_guid) return
            dispatch(
              updateEntry({
                guid: element.entry_guid,
                label: e.target.value,
              })
            )
          }}
        />
      </div>
    ))
  }

  const renderCells = () => {
    const totalCells = colCount * rowCount
    const elementsBySortOrder = new Map(cells.map(el => [el.sort_order, el]))
    return Array.from({ length: totalCells }, (_, i) => {
      const sortOrder = i + 1
      const rowIdx = Math.floor(i / colCount)
      const colIdx = i % colCount
      const el = elementsBySortOrder.get(sortOrder)
      // Column design (e.g. background) lives on the header-row element.
      const columnEl = elementsBySortOrder.get(colIdx + 1)
      const position = getTableCellPosition({
        tableLayout: displayLayout,
        sortOrder,
      })
      const columnBackground = backgroundStyleFromStyles(columnEl?.styles)

      const cell = el ? (
        <PdfElement
          key={`cell-${sortOrder}-${el.entry_guid}`}
          element={{ ...el, position }}
          page={thisPage}
          layout={tableLayout}
        />
      ) : (
        <div
          key={`cell-${sortOrder}-empty`}
          className="static-table--cell"
          style={{
            '--cell-x': `${position.x}px`,
            '--cell-y': `${position.y}px`,
            '--cell-w': `${position.width}px`,
            '--cell-h': `${position.height}px`,
            ...columnBackground,
          }}
        />
      )

      return (
        <Fragment key={`cellgroup-${sortOrder}`}>
          {cell}
          <CellMenu
            position={position}
            options={cellMenuOptions(colIdx, rowIdx)}
            onSelect={option => handleCellMenu(option, colIdx, rowIdx)}
            isOpen={openMenuKey === sortOrder}
            onToggle={() =>
              setOpenMenuKey(prev => (prev === sortOrder ? null : sortOrder))
            }
            onClose={closeMenu}
          />
        </Fragment>
      )
    })
  }

  return (
    <div
      key={tableLayout.id}
      ref={frameRef}
      id={tableWrapperId}
      className={`static-table--frame${
        showTableOrElement ? ' element--is-active' : ''}${
          isTableDragging ? ' --is-dragging-table' : ''
        }${
          hideTableTitle ? ' --with-title--hidden' : ' --with-title'
        }
      }`}
      style={frameVars}
      onClick={onClick}
    >
      {showTableOrElement && (
        <>
          <ResizeHandle handleLocation="top-left" {...tableResizeProps} />
          <ResizeHandle handleLocation="top-right" {...tableResizeProps} />
        </>
      )}
      { hideTableTitle && (
        <Draggable
          id={`table-${tableLayout.id}-handle`}
          classes="static-table--drag-handle"
          data={{
            type: 'tableMove',
            tableLayout,
            page: thisPage,
            sheet_id: keyFieldSheetId,
          }}
          onClick={onClick}
        ><div className="static-table--handle">
            <DragHandleIcon />
          </div>
        </Draggable>
      )}
      { !hideTableTitle && (
        <Draggable
          id={`table-${tableLayout.id}-title`}
          classes="static-table--title"
          data={{
            type: 'tableMove',
            tableLayout,
            page: thisPage,
            sheet_id: keyFieldSheetId,
          }}
          onClick={onClick}
        >
          {keyFieldSheet.description || keyFieldSheet.placeholder || 'Table'}
        </Draggable>
      )}
      {renderColumnHeaderRow()}
      {showTableOrElement && (
        <>
          <ResizeHandle handleLocation="bottom-left" {...tableResizeProps} />
          <ResizeHandle handleLocation="bottom-right" {...tableResizeProps} />
          <StaticTableButton
            className="static-table--add-col static-table--add-col--right"
            onClick={handleAddColumnRight}
          />
          <StaticTableButton
            className="static-table--add-row static-table--add-row--bottom"
            onClick={handleAddRowBelow}
          />
          {renderColumnDividers(displayLayout, tableLayout, tableWrapperId)}
          {showColumnHeader &&
            renderColumnHeaderDivider(
              tableLayout,
              tableWrapperId,
              titleHeight + columnHeaderHeight
            )}
          {renderRowDividers(displayLayout, tableLayout, tableWrapperId, {
            titleHeight,
            columnHeaderHeight,
          })}
        </>
      )}
      {renderCells()}
    </div>
  )
}
